import { useEffect, useState } from 'react'
import { useWebSocket } from '@/hooks/useWebSocket'
import { Activity, TrendingUp, Shield, DollarSign, Wifi, WifiOff } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { MetricsUpdateData } from '@/services/websocket'

interface Metrics extends MetricsUpdateData {
  last_updated: string
}

export function RealTimeMetrics() {
  const [metrics, setMetrics] = useState<Metrics>({
    leads_today: 0,
    fraud_detected: 0,
    amount_saved: 0,
    detection_rate: 0,
    active_alerts: 0,
    last_updated: new Date().toISOString()
  })
  
  const [previousMetrics, setPreviousMetrics] = useState<Metrics>(metrics)
  const [pulseMetric, setPulseMetric] = useState<string | null>(null)
  
  const { isConnected, subscribe } = useWebSocket()
  
  useEffect(() => {
    // Load initial demo data
    const initialMetrics: Metrics = {
      leads_today: 247,
      fraud_detected: 78,
      amount_saved: 23400,
      detection_rate: 94.2,
      active_alerts: 3,
      last_updated: new Date().toISOString()
    }
    setMetrics(initialMetrics)
    setPreviousMetrics(initialMetrics)
    
    const unsubscribe = subscribe('metrics_update', (data: MetricsUpdateData) => {
      setPreviousMetrics(metrics)
      const newMetrics: Metrics = {
        ...data,
        last_updated: new Date().toISOString()
      }
      setMetrics(newMetrics)
      
      // Determine which metric changed most significantly
      const changes = {
        leads: data.leads_today - metrics.leads_today,
        fraud: data.fraud_detected - metrics.fraud_detected,
        saved: data.amount_saved - metrics.amount_saved,
        rate: data.detection_rate - metrics.detection_rate,
        alerts: data.active_alerts - metrics.active_alerts
      }
      
      // Find the metric with the largest positive change
      const maxChange = Math.max(...Object.values(changes))
      if (maxChange > 0) {
        const changedMetric = Object.entries(changes).find(([_, value]) => value === maxChange)?.[0]
        if (changedMetric) {
          setPulseMetric(changedMetric)
          setTimeout(() => setPulseMetric(null), 1000)
        }
      }
    })
    
    return unsubscribe
  }, [subscribe, metrics])
  
  // Format numbers with animation
  const AnimatedNumber = ({ 
    value, 
    previousValue, 
    format = 'number',
    decimals = 0 
  }: {
    value: number
    previousValue: number
    format?: 'number' | 'currency' | 'percentage'
    decimals?: number
  }) => {
    const [displayValue, setDisplayValue] = useState(previousValue)
    
    useEffect(() => {
      const duration = 1000
      const steps = 30
      const stepValue = (value - previousValue) / steps
      const stepTime = duration / steps
      
      let currentStep = 0
      const timer = setInterval(() => {
        currentStep++
        if (currentStep <= steps) {
          setDisplayValue(previousValue + (stepValue * currentStep))
        } else {
          setDisplayValue(value)
          clearInterval(timer)
        }
      }, stepTime)
      
      return () => clearInterval(timer)
    }, [value, previousValue])
    
    const formatValue = (val: number) => {
      switch (format) {
        case 'currency':
          return `$${Math.round(val).toLocaleString()}`
        case 'percentage':
          return `${val.toFixed(decimals)}%`
        default:
          return Math.round(val).toLocaleString()
      }
    }
    
    return <span>{formatValue(displayValue)}</span>
  }
  
  const metricCards = [
    {
      id: 'leads',
      title: 'Leads Today',
      value: metrics.leads_today,
      previousValue: previousMetrics.leads_today,
      icon: Activity,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      format: 'number' as const
    },
    {
      id: 'fraud',
      title: 'Fraud Detected',
      value: metrics.fraud_detected,
      previousValue: previousMetrics.fraud_detected,
      icon: Shield,
      color: 'text-red-500',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      format: 'number' as const
    },
    {
      id: 'saved',
      title: 'Amount Saved',
      value: metrics.amount_saved,
      previousValue: previousMetrics.amount_saved,
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      format: 'currency' as const
    },
    {
      id: 'rate',
      title: 'Detection Rate',
      value: metrics.detection_rate,
      previousValue: previousMetrics.detection_rate,
      icon: TrendingUp,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      format: 'percentage' as const,
      decimals: 1
    }
  ]
  
  const getChangeIndicator = (current: number, previous: number) => {
    const change = current - previous
    if (change === 0) return null
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        className={`text-xs font-medium ${
          change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}
      >
        {change > 0 ? '↗' : '↘'} {Math.abs(change)}
      </motion.div>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Real-Time Metrics</h3>
        <div className="flex items-center gap-2 text-sm">
          {isConnected ? (
            <>
              <Wifi className="h-4 w-4 text-green-500" />
              <span className="text-green-600 dark:text-green-400">Live</span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Offline</span>
            </>
          )}
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
            }`}
          />
        </div>
      </div>
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((card) => (
          <motion.div
            key={card.id}
            className={`bg-card border rounded-lg shadow-sm p-6 transition-all duration-300 ${
              pulseMetric === card.id ? 'ring-2 ring-primary/50 shadow-lg' : ''
            }`}
            animate={{
              scale: pulseMetric === card.id ? [1, 1.02, 1] : 1
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-2">
                  {card.title}
                </p>
                <div className="text-2xl font-bold text-foreground">
                  <AnimatedNumber
                    value={card.value}
                    previousValue={card.previousValue}
                    format={card.format}
                    decimals={card.decimals}
                  />
                </div>
                
                {/* Change Indicator */}
                <div className="mt-1 h-5">
                  <AnimatePresence>
                    {getChangeIndicator(card.value, card.previousValue)}
                  </AnimatePresence>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg ${card.bgColor}`}>
                <card.icon className={`h-6 w-6 ${card.color}`} />
              </div>
            </div>
            
            {/* Pulse Animation Overlay */}
            <AnimatePresence>
              {pulseMetric === card.id && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.3, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1 }}
                  className="absolute inset-0 bg-primary/10 rounded-lg pointer-events-none"
                />
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
      
      {/* Last Updated */}
      <div className="text-xs text-muted-foreground text-center">
        Last updated: {new Date(metrics.last_updated).toLocaleTimeString()}
        {isConnected && (
          <span className="ml-2 text-green-600 dark:text-green-400">
            • Auto-updating
          </span>
        )}
      </div>
    </div>
  )
}